<template>
  <v-col cols="12" md="4" sm="6">
    <div class="text-center">
      <v-dialog v-model="dialog" width="800">
        <v-card>
          <v-card-title class="text-h5 grey lighten-2"> Preview </v-card-title>
          <!--BEGIN display attachment-->
          <v-card-text v-if="dialog" class="attachment pa-0">
            <!--BEGIN display pdf-->
            <div v-if="base64Ext === 'pdf'">
              <!-- PDF Viewer Iframe (Desktop) / Mobile Placeholder -->
              <div
                class="pa-0 elevation-1"
                :style="{
                  height: isMobile ? 'auto' : '70vh',
                  minHeight: isMobile ? '300px' : '600px',
                  position: 'relative',
                  background: isMobile ? '#ffffff' : '#525659'
                }"
              >
                <!-- Loader while Blob URL is being created/loaded -->
                <div v-if="!pdfUrl" class="pa-4 white" style="height: 100%">
                  <v-skeleton-loader type="image" height="100%" />
                </div>

                <template v-else>
                  <!-- Mobile Optimized View -->
                  <div
                    v-if="isMobile"
                    class="pa-12 text-center d-flex flex-column align-center justify-center"
                  >
                    <v-icon size="100" color="primary lighten-4"
                      >mdi-file-pdf-box</v-icon
                    >
                    <div
                      class="mt-6 grey--text text--darken-2 headline font-weight-bold"
                    >
                      Document Preview
                    </div>
                    <div class="mt-2 grey--text subtitle-1 mb-8">
                      Your document is ready. Since you are on a mobile device,
                      please use the button below to view it.
                    </div>
                    <v-btn
                      x-large
                      color="primary"
                      class="text-none px-8 rounded-lg shadow-lg"
                      @click="openPdfInNewTab"
                    >
                      <v-icon left>mdi-eye</v-icon>
                      Open Document
                    </v-btn>
                    <div class="mt-4 grey--text caption">
                      The document will open in your device's native PDF viewer.
                    </div>
                  </div>

                  <!-- Desktop Iframe View -->
                  <iframe
                    v-else
                    :src="pdfUrl"
                    width="100%"
                    height="100%"
                    frameborder="0"
                    style="border: none; display: block"
                    title="PDF Viewer"
                    loading="lazy"
                  ></iframe>
                </template>
              </div>
            </div>
            <!--END display pdf-->
            <!--BEGIN display image-->
            <div v-else-if="isImage">
              <v-img
                :src="
                  base64.length !== 0
                    ? `data:${mimeType};base64, ${base64}`
                    : imageUrl
                "
                class="pa-9"
              />
            </div>
            <div v-else class="pa-4">
              <div class="d-flex flex-column">
                <v-btn
                  class="primary align-self-center mt-3"
                  @click="download(base64)"
                >
                  Download File
                  <v-icon class="mx-2">cloud_download</v-icon>
                </v-btn>
              </div>
            </div>
            <!--END display image-->
          </v-card-text>
          <!--END display attachment-->
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn color="error" text v-on:click="setDialog = false">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-col>
</template>
<script>
import helpers from "@kinetics254/cassandra-base/utilities/helpers/helper";
import FileMixin from "@kinetics254/cassandra-base/mixins/FileMixin.js";

export default {
  mixins: [FileMixin],
  name: "AttachmentPreview",
  props: {
    base64: String,
    dialog: Boolean,
  },

  data: function () {
    return {
      imageUrl: "",
      mimeType: "",
      base64Ext: "",
      pdfUrl: null,
    };
  },
  computed: {
    setDialog: {
      /**
       * Open dialog
       * @returns {boolean}
       */
      get() {
        return this.dialog;
      },
      /**
       * Close dialog
       * @param val
       */
      set(val) {
        this.$emit("close-dialog", val);
      },
    },
    isImage() {
      return /png|jpeg|xml|gif|svg|jpg/.test(this.base64Ext);
    },
    isMobile() {
      // Production-grade mobile detection
      return (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        ) || window.innerWidth <= 768
      );
    },
  },
  methods: {
    /**
     * Get image mime type
     * @param base64
     * @returns {*}
     */
    getMimeType(base64) {
      helpers.getFileType(base64).then((res) => {
        this.mimeType = res.mime;
        this.base64Ext = res.ext;
        if (this.base64Ext === "pdf") {
          this.generatePdfUrl(base64);
        }
      });
    },
    generatePdfUrl(source) {
      if (this.pdfUrl && this.pdfUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.pdfUrl);
      }

      if (!source) {
        this.pdfUrl = null;
        return;
      }

      try {
        let base64Data = source;
        if (base64Data.includes("base64,")) {
          base64Data = base64Data.split("base64,")[1];
        }

        const binaryString = window.atob(base64Data.trim());
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: "application/pdf" });
        this.pdfUrl = URL.createObjectURL(blob);
      } catch (e) {
        console.error("Error generating PDF URL", e);
        this.pdfUrl = null;
      }
    },
    openPdfInNewTab() {
      if (!this.pdfUrl) return;
      window.open(this.pdfUrl, "_blank");
    },
    /**
     * Display attachment
     * @returns {string|string}
     */
    displayAttachment() {
      if (this.base64.length !== 0) {
        return this.base64
          ? `data:${this.mimeType};base64, ${this.base64}`
          : this.imageUrl;
      }
    },
    getFileExt(fileLink) {
      return fileLink.split(".")[1];
    },
    getFileLink(fileLink) {
      return process.env.VUE_APP_FILE_API + fileLink;
    },
    downloadLink(fileLink) {
      const a = document.createElement("a");
      a.href = process.env.VUE_APP_FILE_API + fileLink;
      a.download = process.env.VUE_APP_FILE_API + fileLink;
      a.click();
    },
    download(item) {
      var a = document.createElement("a");
      helpers.getFileType(item);
      a.href = `data:${this.mimeType};base64,${item}`;
      a.download = `Attachment.${this.base64Ext}`;
      a.click();
    },
  },
  watch: {
    base64: {
      immediate: true,
      handler(n, o) {
        if (n && n !== "") {
          this.getMimeType(n);
        }
      },
    },
  },
  beforeDestroy() {
    if (this.pdfUrl && this.pdfUrl.startsWith("blob:")) {
      URL.revokeObjectURL(this.pdfUrl);
    }
  },
};
</script>
<style scoped>
.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}
</style>
